import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import {
  projectsAtom,
  projectByIdAtom,
  updateProjectAtom,
  deleteProjectAtom
} from '../../atoms/v0/projectAtoms';
import {
  tasksAtom,
  createTaskAtom,
  updateTaskAtom,
  deleteTaskAtom
} from '../../atoms/v0/taskAtoms';
import { backgroundRefreshingAtom } from '../../atoms/v0/uiAtoms';
import { ProjectService } from '../../services/projectService';
import { TaskService } from '../../services/taskService';
import { Project } from '../../types/project';
import { Task, CreateTaskData, UpdateTaskData } from '../../types/task';
import { preventSuspense } from '../../utils/jotaiUtils';

export function useProjectDetail(projectId: string | undefined) {
  const { t } = useTranslation(['v0/projects', 'common']);
  const [projects] = useAtom(projectsAtom);
  const [getProjectById] = useAtom(projectByIdAtom);
  const [tasks] = useAtom(tasksAtom);

  // Jotai Actions
  const [, updateProject] = useAtom(updateProjectAtom);
  const [, deleteProject] = useAtom(deleteProjectAtom);
  const [, createTask] = useAtom(createTaskAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const [, deleteTask] = useAtom(deleteTaskAtom);

  // 本地UI状态
  const [project, setProject] = useState<Project | null>(null);
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [context, setContext] = useState('');
  const navigate = useNavigate();
  const toast = useToast();
  const [isBackgroundRefreshing] = useAtom(backgroundRefreshingAtom);

  // 从API加载项目详情（备用）
  const fetchProjectFromApi = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const data = await ProjectService.getProject(id);
      setProject(data);
      setTitle(data.title);
      setContext(data.context || '');
    } catch (error) {
      console.error('Error fetching project from API:', error);
      toast({
        title: t('v0/projects:errors.loadFailed'),
        description: error instanceof Error ? error.message : t('common:errors.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // 从API加载项目任务（备用）
  const fetchProjectTasksFromApi = useCallback(async (id: string) => {
    try {
      setTasksLoading(true);
      const fetchedTasks = await TaskService.getTasks();
      const filteredTasks = fetchedTasks.filter(task => task.projectId === id);
      setProjectTasks(filteredTasks);
    } catch (error) {
      console.error('Error fetching tasks from API:', error);
      toast({
        title: t('v0/projects:errors.loadTasksFailed'),
        description: error instanceof Error ? error.message : t('common:errors.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTasksLoading(false);
    }
  }, [toast]);

  // 从Jotai状态加载项目详情
  const loadProject = useCallback((id: string) => {
    try {
      // 使用 preventSuspense 包装 getProjectById 调用，避免 Suspense 错误
      const projectData = preventSuspense(getProjectById(id));
      if (projectData) {
        setProject(projectData);
        setTitle(projectData.title);
        setContext(projectData.context || '');
        setLoading(false);
      } else {
        // 如果Jotai中没有找到项目，则从API加载
        fetchProjectFromApi(id);
      }
    } catch (error) {
      console.error('Error loading project from Jotai state:', error);
      // 如果读取 Jotai 状态出错，从 API 加载
      fetchProjectFromApi(id);
    }
  }, [getProjectById, fetchProjectFromApi]);

  // 从Jotai状态加载项目任务
  const loadProjectTasks = useCallback((id: string) => {
    try {
      // 使用 preventSuspense 包装 tasks 读取，避免 Suspense 错误
      const currentTasks = preventSuspense(tasks);
      if (currentTasks && currentTasks.length > 0) {
        const filteredTasks = currentTasks.filter(task => task.projectId === id);
        setProjectTasks(filteredTasks);
        setTasksLoading(false);
      } else {
        // 如果Jotai中没有任务，则从API加载
        fetchProjectTasksFromApi(id);
      }
    } catch (error) {
      console.error('Error loading tasks from Jotai state:', error);
      // 如果读取 Jotai 状态出错，从 API 加载
      fetchProjectTasksFromApi(id);
    }
  }, [tasks, fetchProjectTasksFromApi]);

  // 初始化数据
  useEffect(() => {
    if (projectId) {
      loadProject(projectId);
      loadProjectTasks(projectId);
    }
  }, [projectId, loadProject, loadProjectTasks]);

  // 处理保存项目
  const handleSave = async () => {
    if (!projectId || !project) return;

    try {
      setLoading(true);
      // 使用 preventSuspense 包装 updateProject 调用，避免 Suspense 错误
      const updateData = {
        id: projectId,
        title,
        context,
      };

      const success = await updateProject(updateData);

      if (success) {
        // 更新本地状态
        setProject({
          ...project,
          title,
          context
        });
        setIsEditing(false);
        toast({
          title: t('v0/projects:messages.saveSuccess'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('v0/projects:errors.saveFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error saving project:', error);
      toast({
        title: t('v0/projects:errors.saveFailed'),
        description: error instanceof Error ? error.message : t('common:errors.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // 处理删除项目
  const handleDelete = async (cascadeDelete: boolean = false) => {
    if (!projectId) return;

    try {
      setLoading(true);
      const success = await deleteProject(projectId, cascadeDelete);

      if (success) {
        toast({
          title: t('v0/projects:messages.deleteSuccess'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        navigate('/v0/projects');
      } else {
        toast({
          title: t('v0/projects:errors.deleteFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: t('v0/projects:errors.deleteFailed'),
        description: error instanceof Error ? error.message : t('common:errors.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // 处理创建任务
  const handleCreateTask = async (data: CreateTaskData) => {
    if (!projectId) return false;

    try {
      const success = await createTask({
        ...data,
        projectId
      });

      if (success) {
        toast({
          title: t('v0/projects:messages.createTaskSuccess'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // 重新加载任务列表
        loadProjectTasks(projectId);
        return true;
      } else {
        toast({
          title: t('v0/projects:errors.createTaskFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return false;
      }
    } catch (error) {
      toast({
        title: t('v0/projects:errors.createTaskFailed'),
        description: error instanceof Error ? error.message : t('common:errors.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  };

  // 处理标记任务完成
  const handleToggleTaskDone = async (task: Task, done: boolean) => {
    try {
      const updatedTask: UpdateTaskData = {
        id: task.id,
        done
      };

      const success = await updateTask(updatedTask);

      if (success && projectId) {
        // 重新加载任务列表
        loadProjectTasks(projectId);
      }
    } catch (error) {
      toast({
        title: t('v0/projects:errors.updateTaskFailed'),
        description: error instanceof Error ? error.message : t('common:errors.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // 处理删除任务
  const handleDeleteTask = async (taskId: string) => {
    if (!projectId) return;

    try {
      const success = await deleteTask(taskId);

      if (success) {
        toast({
          title: t('v0/projects:messages.deleteTaskSuccess'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // 重新加载任务列表
        loadProjectTasks(projectId);
      } else {
        toast({
          title: t('v0/projects:errors.deleteTaskFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: t('v0/projects:errors.deleteTaskFailed'),
        description: error instanceof Error ? error.message : t('common:errors.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // 格式化日期
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 修改标题
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
  };

  // 修改描述
  const handleContextChange = (newContext: string) => {
    setContext(newContext);
  };

  // 切换编辑模式
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  // 取消编辑
  const cancelEdit = () => {
    if (project) {
      setTitle(project.title);
      setContext(project.context || '');
    }
    setIsEditing(false);
  };

  return {
    project,
    projectTasks,
    loading,
    tasksLoading,
    isEditing,
    title,
    context,
    isBackgroundRefreshing,

    // 统计数据
    tasksTotal: projectTasks.length,
    tasksVital: projectTasks.filter(task => task.vital).length,
    tasksUrgent: projectTasks.filter(task => task.urgent).length,

    // 方法
    formatDate,
    handleSave,
    handleDelete,
    handleCreateTask,
    handleToggleTaskDone,
    handleDeleteTask,
    handleTitleChange,
    handleContextChange,
    toggleEditMode,
    cancelEdit,

    // 导航到任务详情
    handleSelectTask: (task: Task) => navigate(`/v0/tasks/${task.id}`),
    navigateToProjects: () => navigate('/v0/projects')
  };
}